/**
 * **S2.5 + S3 — the attack's two pre-roll folds.** Two adjacent stages of `attack.ts`'s
 * `resolveSingleTarget` pipeline, both running before any dice are drawn:
 *
 * - {@link foldAttackCover} (S2.5) folds the target's terrain cover degree into one
 *   net-accuracy term, and settles the `degree` itself.
 * - {@link foldAttackModifiers} (S3) folds the entity overlay's attacker-side and target-side
 *   modifiers into the thirteen facets the rest of the resolution reads — the two accuracy sums
 *   and their per-source attribution, the target-side damage facets (Shredded's ignore-armor,
 *   Exposed's doubling, Sniper's Mark's structure substitution, Stunned's Evasion cap, Invisible's
 *   miss chance), the attacker-side
 *   damage-type override and the attack roll's replacements, plus the advisory warnings and the consumed
 *   resource caps the caller splices onto its own accumulators.
 *
 * **The cover fold used to run at S6, AFTER the modifier fold, and was moved ahead of it by
 * `npc-features-fielded-roster` Phase 8.1**: the modifier fold's predicate context now carries
 * the settled cover degree as an attack-family event fact ({@link
 * import("../effects/predicate").AttackEvent.cover}), so a `modifier` clause can be gated on
 * `match: { family: "attack", cover: "none" }` (Sniper's Mark, Core p.309). The move is
 * observationally inert: `foldAttackCover` is pure, draws no dice and emits no step, so
 * computing it earlier — including on the two paths that used to return before reaching it (the
 * variable-damage-type prompt exit and Invisible's miss-chance coin) — changes no step, no
 * warning and no RNG sequence. Nothing may reorder them back.
 *
 * They stay two folds rather than one because a `needs_decision` exit and a dice draw still sit
 * between the modifier fold and the roll in `resolveSingleTarget` — the variable-damage-type
 * prompt, then Invisible's pre-roll miss-chance coin. The caller owns that ordering; neither
 * fold may be moved across the d20.
 *
 * Both are pure over their arguments: they read `state` and return a record, mutating nothing,
 * drawing no dice, emitting no step, and carrying no early exit. That is why they are the stages
 * the decomposition moved first — nothing about resolution ORDER rides on them beyond "before
 * the d20," which the caller keeps.
 *
 * The one ordering fact that does matter and is NOT visible from here: {@link
 * foldAttackModifiers}'s `chosen` argument is the resolution ledger's `choose` record, which
 * `attack.ts`'s `fireAttackDeclared` has already populated by the time that fold runs. That is
 * what lets an `on_attack_declared` ammo choice gate THIS SAME attack's
 * `override_attack_damage_type`.
 */

import type { AttackVerb, Cell, Combatant, DamageType, EncounterState, WeaponProfile } from "@lancer-vtt/domain";
import { hasStatus, outsideAttackDifficultyAreas, SOFT_COVER_STATUS, terrainVisibility, worldFor } from "@lancer-vtt/domain";
import { auraFacetSource } from "../aura";
import type { RequestAttack } from "@lancer-vtt/shared";
import { isRangedWeaponAttack } from "./weaponMode";
import {
  attackPredicateContext,
  combatantEffectIds,
  foldModifiers,
  footprintCells,
  gatherConferredModifiers,
  seedModifiers,
  seedPilotModifiers,
  seedSystemModifiers,
  seedWeaponModifiers,
  alliedCoverCells,
  deniesSoftCover,
  grantsSoftCover,
  type ChoiceSource,
  type LoadedOverlay,
} from "../effects";
import { scopeAttackModifiers } from "../effects/modifiers";
import type { PredicateContext } from "../effects/predicate";
import { grapplePartner } from "../grapple";
import { attemptMissChances, rollReplacements, type MissChance } from "../events/rollReplacements";
import type { Replacement } from "../events/loop";

/**
 * What {@link foldAttackModifiers} settles for the rest of the resolution.
 *
 * Every field carries its no-overlay default when the resolution opts out of the effects engine
 * (or no active modifier grants that facet) — the two accuracy sums are `0`, the booleans
 * `false`, the arrays empty, and the three genuinely-optional facets `undefined`.
 * The one exception is a shield dome's +1 Difficulty, which is map state and folds into
 * `targetAccuracy` and `accuracyContributions` either way.
 */
export type AttackModifierFacets = {
  /** The attacker-side accuracy sum (Impaired = −1, Accurate {VAL}, a conferred boon, …). */
  attackerAccuracy: number;
  /** The target-side accuracy sum (Lock On = +1, a defensive talent modifier, …). */
  targetAccuracy: number;
  /**
   * `session-one-combat-readiness` Phase 2.2: the per-source breakdown behind
   * {@link attackerAccuracy}/{@link targetAccuracy} — one entry per `BoundModifier` that
   * actually folded on EITHER side, concatenated in attacker-then-target order. Populated
   * only when an overlay is set; stays empty for a no-overlay resolution (there is no
   * modifier fold to attribute).
   */
  accuracyContributions: { name: string; carrier: string; side: "attacker" | "target"; value: number }[];
  /** Target-side `consumeOnHit` resource keys — read by the hit branch's consume pass. */
  consumeOnHit: string[];
  /** A target-side modifier may make the attack ignore the carrier's Armor (Shredded). */
  ignoreTargetArmor: boolean;
  /** An attacker-side modifier may make the attack ignore cover (Vanguard 2). */
  ignoreCover: boolean;
  /** A target-side modifier may double the physical damage against the carrier (Exposed, A3). */
  doubleDamage: boolean;
  /**
   * A target-side modifier may replace the hit's whole damage application with 1 structure damage
   * (Sniper's Mark, `npc-features-fielded-roster` Phase 8.1) — `false` when nothing marks the
   * target (the common case). Read by S9b ({@link
   * import("./damageApply").applyHitDamage}); see {@link
   * import("../effects/atoms").ModifierContribution.substituteStructureDamage} for what the
   * substitution skips and the two gates the read site applies before taking it.
   */
  substituteStructureDamage: boolean;
  /**
   * Stunned's Evasion cap (`status-catalog-completion` P2.2) — target-side, `undefined` when
   * no active modifier grants it (the common case). Read by the caller's defense computation.
   */
  evasionCap: number | undefined;
  /**
   * The miss-chance clauses that hold on this attack (Invisible, Mirage; `event-engine-to-hit`
   * 3.1), the target's own first: the attack attempt's replacements, and the forecast's chance. Empty
   * in the common case.
   */
  missChances: MissChance[];
  /**
   * LL0 DSL Primitives III, P2, `override_attack_damage_type` (t_walking_armory:1's ammo
   * rounds) — an ATTACKER-side modifier overriding the resolution's damage type outright.
   * `undefined` when no active modifier overrides it (the common case).
   */
  overrideDamageType: DamageType | undefined;
  /**
   * The attack roll's replacements (`event-engine-to-hit` R10), in the order the loop applies
   * them: Spotter's Particularize (`advantage`, conferred by an adjacent ally). Matched against
   * the same pre-roll facts as the attacker-side fold, the attack's verb included. Empty when none applies.
   */
  attackRoll: Replacement[];
  /** An ATTACKER-side modifier making the attack AP (Walking Armory's SABOT). `false` when none does. */
  armorPiercing: boolean;
  /** The type an ATTACKER-side modifier gives the attack's bonus damage (Walking Armory's HELLFIRE). `undefined` when none does. */
  bonusDamageType: DamageType | undefined;
  /**
   * The advisory warnings this fold produced — the attacker-side action/movement gates
   * (Stunned/Slowed/Immobilized/Prone — Decision 6) and both sides' deferred spatial riders
   * (D10 — Prone's adjacency rider), already prefixed. Returned rather than pushed: the fold
   * stays pure and the caller concatenates onto its own `warnings` accumulator at the seam.
   */
  warnings: string[];
  /**
   * `ll0-dsl-primitives-2` Phase E (`decisions/0085`): the
   * `modifier_consume_resource_on_apply` caps THIS fold actually spent — attacker-side
   * (vanguard's CQB Range-3 Accuracy) then target-side. Returned rather than pushed for the
   * same purity reason as {@link warnings}; the caller owns the resolution-wide accumulator,
   * which the on-hit/on-crit trigger fire also appends to later.
   */
  consumedResourceCaps: { carrier: string; key: string }[];
};

/** Args for {@link foldAttackModifiers} (max-params-3 discipline, `CODE_STYLE.md`). */
export type FoldAttackModifiersArgs = {
  state: EncounterState;
  attacker: Combatant;
  target: Combatant;
  /** The entity overlay S1 resolved. `undefined` ⇒ every facet keeps its default. */
  overlay: LoadedOverlay | undefined;
  /** The attack's `choices`, so a `gm_ruling` in a modifier's condition reads (or, interactively, asks for) the GM's answer. */
  rulings: ChoiceSource | undefined;
  /** The SELECTED firing profile — its frozen `{VAL}` tags seed the attacker-side modifiers. */
  weapon: WeaponProfile;
  rangedAttack: boolean;
  techAttack: boolean;
  weaponType: string | undefined;
  mount: string | undefined;
  firstAttackThisTurn: boolean;
  /** S2's attack verb — what `scopeAttackModifiers` strips an `attackScope`-scoped modifier against. */
  attackVerb: AttackVerb;
  /**
   * S2's composite granted-action id ({@link import("./attackFacts").AttackFacts.actionId}) —
   * the `match.actionId` event fact for this fold, so a `modifier` clause can scope itself to
   * ONE weapon/granted action instead of applying to every attack its carrier makes.
   */
  actionId: string;
  /**
   * S2.5's settled cover degree ({@link foldAttackCover}'s `degree`) — the `match.cover` event
   * fact for this fold. This is the argument that forced the cover fold ahead of this one; see
   * the module doc.
   */
  cover: "none" | "soft" | "hard";
  /**
   * **How many characters this attack targets** — `resolveSingleTarget`'s own count (the area
   * fan-out's settled `targetIds.length`, post-LoS-exclusion; `1` on the single-target spine),
   * threaded so a `modifier` clause's `match` can gate on `targetCount: { ">": 1 }` against the
   * SAME number a `{TARGET_COUNT}` magnitude scales by. Always a real number for an attack fold
   * — never absent — so `targetCount: 1` is a positive match on a single-target attack rather
   * than an unknown.
   */
  targetCount: number;
  /**
   * `LL0 DSL Primitives V`, P1: the resolution ledger's `choose` record — already populated
   * by `attack.ts`'s `fireAttackDeclared` (which runs to completion before this fold), so an
   * `on_attack_declared` ammo choice can gate THIS SAME attack's `override_attack_damage_type`.
   */
  chosen: Record<string, string>;
  /** The action's pending costs (the resolution ledger), for the capped modifiers' affordable check. */
  costs?: PredicateContext["costs"];
  /**
   * The wielder's pre-roll consume answer ({@link import("./weaponConsume").settleConsumeChoice}).
   * `consume` is the `match.consumes` event fact; a `keep` status's own `consumeOnHit` modifier is
   * dropped from the target fold, so a kept Lock On grants no +1. Omitted on the discovery fold
   * that finds the consumable statuses in the first place.
   */
  consumeChoice?: { consume: string[]; keep: string[] };
};

/** The accuracy-contribution name of an `outside_attack_difficulty` area's +1 Difficulty. */
export const SHIELD_DOME = "Shield dome";

export function foldAttackModifiers(args: FoldAttackModifiersArgs): AttackModifierFacets {
  const { state, attacker, target, overlay, rulings, weapon, rangedAttack, techAttack } = args;
  const { weaponType, mount, firstAttackThisTurn, attackVerb, chosen, actionId, cover, targetCount, consumeChoice, costs } = args;

  let attackerAccuracy = 0;
  let targetAccuracy = 0;
  let consumeOnHit: string[] = [];
  let ignoreTargetArmor = false;
  let doubleDamage = false;
  let substituteStructureDamage = false;
  let evasionCap: number | undefined;
  let missChances: MissChance[] = [];
  let overrideDamageType: DamageType | undefined;
  let attackRoll: Replacement[] = [];
  let armorPiercing = false;
  let bonusDamageType: DamageType | undefined;
  let ignoreCover = false;
  let accuracyContributions: { name: string; carrier: string; side: "attacker" | "target"; value: number }[] = [];
  const warnings: string[] = [];
  const consumedResourceCaps: { carrier: string; key: string }[] = [];
  if (overlay) {
    const pctx = attackPredicateContext({
      state,
      attacker: attacker.id,
      target: target.id,
      rulings,
      rangedAttack,
      techAttack,
      weaponType,
      mount,
      firstAttackThisTurn,
      actionId,
      cover,
      targetCount,
      chosen,
      ...(costs !== undefined ? { costs } : {}),
      ...(consumeChoice !== undefined ? { consumes: consumeChoice.consume } : {}),
    });
    // The attack roll's replacements also match the attack's verb, which the modifier fold scopes with `attackScope` instead.
    const rollFacts = attackPredicateContext({ state, attacker: attacker.id, target: target.id, rulings, rangedAttack, techAttack, weaponType, mount, firstAttackThisTurn, actionId, cover, targetCount, chosen, verb: attackVerb, ...(costs !== undefined ? { costs } : {}), ...(consumeChoice !== undefined ? { consumes: consumeChoice.consume } : {}) });
    // A kept consumable status (Lock On) grants nothing to this attack: RAW makes its +1 the
    // price of consuming it.
    const kept = new Set(consumeChoice?.keep ?? []);
    const notKept = (m: { name: string; effect: { mod: { consumeOnHit?: boolean } } }) => !(m.effect.mod.consumeOnHit && kept.has(m.name));
    // A target hostile to a Lotus Projector inside its field "can't benefit from being Invisible"
    // (`deny_invisible`): its Invisible status draws no coin.
    const invisibleDenied = auraFacetSource(state, target, "deny_invisible") !== undefined;
    const atkMods = foldModifiers(
      // The attacker's status modifiers PLUS its firing weapon's `{VAL}` tag modifiers
      // (Accurate, …) PLUS its frozen pilot effects' conditional modifiers (a talent/core-
      // bonus boon — pilot-combat-effects Phase 1) PLUS anything an adjacent same-side ally
      // CONFERS onto it (`LL0 DSL Primitives III` P4, `confer_modifier_to_adjacent_allies` —
      // t_combined_arms:1's Shield of Blades) — all fold into the
      // attacker side of the to-hit math through the SAME path (D3/D7/D8). The pilot-effect
      // gather threads `partner = grapplePartner(state, attacker.id)` (`LL0 DSL Primitives
      // III`, P5, `pilot_modifier_partner_binding` — t_brawler:1's Hold and Lock): the
      // ATTACKER's own bound Grappled partner, if any, so a talent-borne modifier's
      // `target_is who:"partner"` faithfully means "is the target who I'm grappling,"
      // never any Grappled target (the old `has_status(target,'Grappled')` workaround's
      // multi-grapple over-application).
      // `ll0-dsl-primitives-8` D5: `scopeAttackModifiers` strips an `attackScope`-scoped
      // modifier's WHOLE attack-resolution facet domain (`ATTACK_SCOPED_FACETS` — not just
      // `attackerAccuracy`; a partial strip is a silent over-grant) whenever its authored
      // `verb` doesn't match `attackVerb`. Applied to the FULL combined list below (status +
      // weapon/tag + pilot-effect + system + conferred), so every vector `attackerAccuracy`
      // itself already reaches also reaches `attackScope` (see `coverage.ts`'s
      // `REACHABLE_MODIFIER_FACETS` for the traced claim).
      scopeAttackModifiers(
        [
          ...seedModifiers(state, attacker.id, overlay),
          ...seedWeaponModifiers(attacker.id, weapon, overlay),
          ...seedPilotModifiers({
            carrierId: attacker.id,
            pilotEffectIds: combatantEffectIds(attacker),
            overlay,
            partner: grapplePartner(state, attacker.id),
          }),
          // `effects-gather-and-legality-defects` Phase 2, D5: ALSO fold the attacker's frozen
          // equipped-SYSTEM modifiers (a mech-passive attack-fold facet — ms_scanner_swarm's
          // "+1 Accuracy when attacking an adjacent target with a tech attack"). Before this,
          // `seedSystemModifiers`' only wired consumer was `interpretForceSaveRoll` — a system-
          // carried `attackerAccuracy`/`targetAccuracy`/etc. modifier clause was structurally
          // unreachable from the attack fold (defect C), even though `coverage.ts` reported it
          // `mechanized` (defect D, closed alongside this by `REACHABLE_MODIFIER_FACETS`).
          ...seedSystemModifiers(state, attacker.id, overlay),
          ...gatherConferredModifiers(state, attacker.id, overlay),
        ],
        { verb: attackVerb },
      ),
      "attacker",
      pctx,
    );
    const tgtMods = foldModifiers(
      // The target's status modifiers PLUS its frozen pilot effects' target-side modifiers
      // (a defensive talent/core-bonus modifier — pilot-combat-effects Phase 1) PLUS anything
      // an adjacent same-side ally CONFERS onto it (P4 — t_combined_arms:1's Shield of Blades
      // extending its Engaged-cover to adjacent allies). Same `partner` threading as the
      // attacker fold above, sourced from the TARGET's own bound Grappled partner (P5) — a
      // symmetric affordance for a future defensive talent gated on "who I am grappling."
      [
        ...seedModifiers(state, target.id, overlay).filter(notKept),
        ...seedPilotModifiers({
          carrierId: target.id,
          pilotEffectIds: combatantEffectIds(target),
          overlay,
          partner: grapplePartner(state, target.id),
        }),
        // `effects-gather-and-legality-defects` Phase 2, D5: the target-side mirror of the
        // attacker fold above — a defending mech's own equipped SYSTEM can carry a target-side
        // facet (`targetAccuracy`, `consumeOnHit`, `ignoreTargetArmor`, `doubleDamage`,
        // `evasionCap`) exactly like a status or pilot effect can.
        ...seedSystemModifiers(state, target.id, overlay),
        ...gatherConferredModifiers(state, target.id, overlay),
      ],
      "target",
      pctx,
    );
    attackerAccuracy = atkMods.attackerAccuracy;
    targetAccuracy = tgtMods.targetAccuracy;
    // Phase 2.2: the attacker fold's contributions are already attacker-side, the
    // target fold's already target-side (each `foldModifiers` call only ever pushes
    // its own side) — concatenating preserves that provenance with no relabeling.
    accuracyContributions = [...atkMods.accuracyContributions, ...tgtMods.accuracyContributions];
    consumeOnHit = tgtMods.consumeOnHit;
    // LL0 DSL Primitives III, P2 — an attacker-side modifier's damage-type override (t_walking_armory:1's ammo).
    overrideDamageType = atkMods.overrideDamageType;
    // The attack roll's replacements (Spotter's advantage), gathered like the attacker-side fold
    // and matched on the same facts plus the attack's verb.
    attackRoll = rollReplacements({ state, overlay, roller: attacker.id, roll: "attack", weapon, partner: grapplePartner(state, attacker.id), facts: rollFacts });
    // An attacker-side modifier that makes the attack AP (Walking Armory's SABOT).
    armorPiercing = atkMods.armorPiercing;
    // An attacker-side modifier that re-types the attack's bonus damage (Walking Armory's HELLFIRE).
    bonusDamageType = atkMods.bonusDamageType;
    // An attacker-side modifier that makes the attack ignore cover (t_vanguard:2).
    ignoreCover = atkMods.ignoreCover;
    // `ll0-dsl-primitives-2` Phase E: caps consumed by an attacker-side (vanguard's CQB Range-3
    // Accuracy) OR target-side modifier this fold actually applied.
    consumedResourceCaps.push(...atkMods.consumedResourceCaps, ...tgtMods.consumedResourceCaps);
    // A target-side modifier may make the attack ignore the carrier's Armor (Shredded).
    ignoreTargetArmor = tgtMods.ignoreTargetArmor;
    // A target-side modifier may double the physical damage against the carrier (Exposed, A3).
    doubleDamage = tgtMods.doubleDamage;
    // A target-side modifier may substitute 1 structure damage for the whole hit (Sniper's Mark).
    substituteStructureDamage = tgtMods.substituteStructureDamage;
    // Stunned's Evasion cap (P2.2): target-side, read by the caller's defense computation.
    evasionCap = tgtMods.evasionCap;
    // Invisible's and Mirage's pre-roll coin: the attempt's replacements, read off the target.
    missChances = attemptMissChances({ state, overlay, target: target.id, facts: pctx, invisibleDenied });
    // Advisory action/movement gates (Stunned/Slowed/Immobilized/Prone — Decision 6):
    // surfaced when the carrier ATTACKS, never blocking. The resolution proceeds.
    warnings.push(...atkMods.advisories);
    // Deferred spatial riders (D10 — Prone's adjacency rider) noted so a driver knows the
    // math here is partial. Surfaced for both sides, prefixed (the retired table's shape).
    warnings.push(
      ...atkMods.spatialRidersDeferred.map((r) => `attacker: ${r}`),
      ...tgtMods.spatialRidersDeferred.map((r) => `target: ${r}`),
    );
  }

  // A shield dome (`outside_attack_difficulty`, the ENCLAVE-Pattern Support Shield) gives a ranged or
  // melee attack +1 Difficulty when it holds the target and not the attacker, one per dome. It is
  // map state, not overlay content, so it folds even when the resolution opted out of the effects
  // engine, and it rides the target side like a defensive modifier, carried by the dome's source.
  if (!techAttack) {
    const world = worldFor(state);
    const footprints = { attacker: footprintCells(world, attacker), target: footprintCells(world, target) };
    for (const dome of outsideAttackDifficultyAreas(world, state.areaEffects ?? {}, footprints)) {
      targetAccuracy -= 1;
      accuracyContributions.push({ name: SHIELD_DOME, carrier: dome.source, side: "target", value: -1 });
    }
  }

  return {
    attackerAccuracy,
    targetAccuracy,
    accuracyContributions,
    consumeOnHit,
    ignoreTargetArmor,
    ignoreCover,
    doubleDamage,
    substituteStructureDamage,
    evasionCap,
    missChances,
    overrideDamageType,
    attackRoll,
    armorPiercing,
    bonusDamageType,
    warnings,
    consumedResourceCaps,
  };
}

/** What {@link foldAttackCover} settles: the cover degree and its net-accuracy term. */
export type CoverFold = {
  /** The single winning cover degree — cover does NOT stack, so at most one applies. */
  degree: "none" | "soft" | "hard";
  /** The net-accuracy term that degree contributes: soft −1, hard −2, none 0. */
  acc: number;
};

/** Args for {@link foldAttackCover} (max-params-3 discipline, `CODE_STYLE.md`). */
export type FoldAttackCoverArgs = {
  state: EncounterState;
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  /** The SELECTED firing profile — its `range` and frozen tags drive the ranged/Seeking/thrown gate. */
  weapon: WeaponProfile;
  /** S2's melee-action-attack fact (Ram, …), which is cover-exempt like tech is. */
  isActionAttack: boolean;
  /**
   * Blast cover measure-from origin cell (`decisions/0051` D4). `undefined` for every non-Blast
   * resolution → attacker-relative measurement, unchanged.
   */
  measureFrom?: Cell;
  /**
   * The resolution's overlay — the Goliath's Guardian (`coverToAllies`) is read off it.
   * `undefined` when the resolution opted out of the effects engine: no content, no Guardian.
   */
  overlay: LoadedOverlay | undefined;
};

export function foldAttackCover(args: FoldAttackCoverArgs): CoverFold {
  const { state, attacker, target, intent, weapon, isActionAttack, measureFrom, overlay } = args;

  // ── Cover (cover-to-hit-fold, Core p.66): ranged attacks fold the target's terrain
  // cover degree into net accuracy — soft = -1 Difficulty, hard = -2 Difficulty (cover
  // types don't stack; `terrainVisibility` already reports at most one degree). Melee
  // and tech attacks ignore cover entirely (RAW), as does a Seeking-tagged weapon (its
  // RAW text: "ignores cover and doesn't require line of sight") — EXCEPT a thrown melee
  // weapon (`tg_thrown`), which RAW carves back IN ("Thrown attacks follow the rules for
  // melee attacks but are affected by cover"; see `forcesCover` below). Sourced from the SAME
  // visibility seam the browser's per-cell cover advisory (`armedCellSightlines`) and
  // `checkHideCover` already reads (`terrainVisibility`) — never a
  // parallel geometry, so the wall and the hint can't drift. Measured from the attacker's
  // own footprint by default, or the area template's origin cell for a Blast
  // (`measureFrom`, `decisions/0051` D4); Burst/Line/Cone carry no such anchor and fall back to the
  // attacker, exactly like a single-target shot. Computed PRE-DRAW (before any dice) as just
  // another net-accuracy term, so replay/undo stay byte-stable — no extra dice, no
  // RNG-sequence change. A GM `gm_force` shot reaches `resolve` as its plain wrapped
  // `request_attack`, so cover math applies to it exactly the same as a rules-following shot —
  // it is math, not a gate.
  let coverDegree: "none" | "soft" | "hard" = "none";
  let coverAcc = 0;
  const isSeeking = weapon.tags?.some((t) => t.id === "tg_seeking") ?? false;
  // Thrown (`authorable-tag-facets` Phase 3, RAW: "Thrown attacks follow the rules for melee
  // attacks but are affected by cover") — the mirror-image special case to Seeking: Seeking
  // EXEMPTS an otherwise-ranged weapon from cover, tg_thrown FORCES cover onto an otherwise-
  // exempt melee weapon. Same frozen-profile-tag idiom as `isSeeking` right above (one inline
  // tag check, not a rewrite of the ranged/melee gate).
  const forcesCover = weapon.tags?.some((t) => t.id === "tg_thrown") ?? false;
  if (!intent.tech && !isActionAttack && !isSeeking && (isRangedWeaponAttack({ state, attacker, intent, weapon }) || forcesCover)) {
    const world = worldFor(state);
    const originFp = measureFrom !== undefined ? [measureFrom] : footprintCells(world, attacker);
    const targetFp = footprintCells(world, target);
    // A "Soft Cover" STATUS grants soft cover (`mw_veil_rifle`'s ally shroud, `target_next_turn_end`
    // — RAW Core p.66 soft cover = +1 Difficulty). Unlike terrain/object/area cover this arrives as
    // a status on the target, not a cover cell on the line, so it never shows up in
    // `terrainVisibility().cover` — it is folded into the SAME single cover degree here. RAW: cover
    // DOES NOT stack — the best degree wins — so the status only ever FLOORS the degree to soft,
    // never adds a parallel −1 term (a target already in hard terrain cover stays −2, not −3).
    // Inherits the ranged-only / Seeking-exempt / thrown-forced gate above for free, exactly as
    // cover should (melee and tech ignore cover). `terrainVisibility` also folds live
    // `state.areaEffects` (a smoke cloud etc.) into the SAME `.cover` read below via its own
    // occupancy floor (D3/D4, `projects/expiring-cell-effects.md`) — so once an area exists in
    // state, standing in it reaches the to-hit fold through this one call, no separate branch.
    // An allied drone's aura floors it the same way (`ally_soft_cover`, Hive's "Allied characters at
    // least partially within the affected area gain soft cover, as does the hive drone").
    // Markerlight's mark ("cannot benefit from soft cover") strips every soft-cover source; hard stays.
    const noSoftCover = overlay !== undefined && deniesSoftCover({ state, targetId: target.id, attackerId: attacker.id, overlay }) !== undefined;
    // Skirmisher 1 / Combined Arms 1 ("you count as having soft cover") floor it the same way.
    const grantedSoftCover = overlay !== undefined && grantsSoftCover({ state, targetId: target.id, attackerId: attacker.id, overlay }) !== undefined;
    const softCoverFloor = !noSoftCover && (hasStatus(target.statuses, SOFT_COVER_STATUS) || grantedSoftCover || auraFacetSource(state, target, "ally_soft_cover") !== undefined);
    if (originFp.length > 0 && targetFp.length > 0) {
      const view = terrainVisibility({
        space: world,
        terrain: state.terrain,
        from: originFp,
        to: targetFp,
        edges: state.edges,
        combatants: state.combatants,
        excludeCoverFrom: target.id,
        areaEffects: state.areaEffects,
        // A character that is hard cover for its allies (the Goliath's Guardian).
        ...(overlay !== undefined ? { extraHardCoverCells: alliedCoverCells({ state, targetId: target.id, attackerId: attacker.id, overlay }) } : {}),
      });
      coverDegree = view.cover === "hard" ? "hard" : (view.cover === "soft" && !noSoftCover) || softCoverFloor ? "soft" : "none";
      coverAcc = coverDegree === "soft" ? -1 : coverDegree === "hard" ? -2 : 0;
    }
  }

  return { degree: coverDegree, acc: coverAcc };
}

/** Named args for {@link damageRollReplacements}: the pre-roll fold's facts plus the outcome that causes the damage roll. */
export type DamageRollReplacementsArgs = Omit<FoldAttackModifiersArgs, "consumeChoice"> & {
  /** The statuses this attack consumes. */
  consumes: string[];
  /** The attack's outcome: the standing roll's verdict and whether its kept d20 showed 20. */
  outcome: { hit: boolean; crit: boolean; natural20: boolean };
};

/**
 * The damage roll's replacements (`event-engine-to-hit` R10): Brutal's Predator (`maximize`,
 * matched on `natural20` and `crit`). They match on the attack's facts as the attacker-side fold
 * sees them, plus the outcome that causes the damage roll, so a clause gated on the natural 20 reads
 * the roll that stands.
 */
export function damageRollReplacements(args: DamageRollReplacementsArgs): Replacement[] {
  const { state, attacker, target, overlay, rulings, weapon, rangedAttack, techAttack, weaponType, mount, firstAttackThisTurn, attackVerb, chosen, actionId, cover, targetCount, consumes, outcome, costs } = args;
  const facts = attackPredicateContext({ state, attacker: attacker.id, target: target.id, rulings, rangedAttack, techAttack, weaponType, mount, firstAttackThisTurn, actionId, cover, targetCount, chosen, consumes, natural20: outcome.natural20, verb: attackVerb, hit: outcome.hit, crit: outcome.crit, ...(costs !== undefined ? { costs } : {}) });
  return rollReplacements({ state, overlay, roller: attacker.id, roll: "damage", weapon, partner: grapplePartner(state, attacker.id), facts });
}
